// ═══════════════════════════════════════════════════════════════
// POLLEN CARD — Renewals (Anniversary Donors)
// Path: src/components/dashboard/pollen/PollenRenewals.jsx
//
// "Time to renew the relationship."
// Surfaces donors whose last gift anniversary falls within
// ±30 days of today (i.e. last gift was 335–395 days ago).
//
// Workflow stages track automatically via contact_logs
// with purpose = "gift_renewal":
//   0 contacts → Identify (amber)
//   1 contact  → Impact Report sent (blue)
//   2+ contacts → Ask made (green)
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from "react";
import { CalendarClock, X, Info, Sprout, Check } from "lucide-react";
import { useApp } from "../../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../../constants/appConfig";
import { supabase } from "../../../lib/supabase"; // Still needed for contact_logs query
import {
  getFiscalYearBoundaries,
  summarizeGiftsByFy,
} from "../../../utils/fiscalYear";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const STAGE_COLORS = {
  identify: "#f59e0b",
  impact: "#3b82f6",
  ask: "#10b981",
};

const STAGE_LABELS = {
  identify: "Identify",
  impact: "Impact Report",
  ask: "Ask",
};

// Compute the renewal window boundaries as date strings (YYYY-MM-DD).
// A donor is in-window if their last gift was 335–395 days ago,
// meaning the 1-year anniversary falls within ±30 days of today.
const getRenewalWindow = () => {
  const today = new Date();

  const windowStart = new Date(today);
  windowStart.setDate(windowStart.getDate() - 395);

  const windowEnd = new Date(today);
  windowEnd.setDate(windowEnd.getDate() - 335);

  return {
    windowStartStr: windowStart.toISOString().split("T")[0],
    windowEndStr: windowEnd.toISOString().split("T")[0],
  };
};

// Days between two date strings (positive = future, negative = past)
const daysFromToday = (dateStr) => {
  const target = new Date(dateStr + "T12:00:00");
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
};

export const PollenRenewals = ({ onClose }) => {
  const {
    palette,
    navigate,
    dataVersion,
    members,
    cultivationQueue,
    addContactLog,
    allGifts,
    chapterSettings,
  } = useApp();
  const [renewalDonors, setRenewalDonors] = useState([]);
  const [showInfo, setShowInfo] = useState(false);

  // Quick-action: log a gift_renewal contact to advance the stage
  const handleMarkContacted = async (donor, event) => {
    event.stopPropagation();
    await addContactLog(donor.memberId, {
      type: "note",
      direction: "outbound",
      subject: "Renewal outreach",
      notes: "",
      purpose: ["gift_renewal"],
    });
    // Optimistically advance stage in UI
    setRenewalDonors((prev) =>
      prev.map((d) => {
        if (d.memberId !== donor.memberId) return d;
        const newCount = d.renewalContactCount + 1;
        let stage;
        if (newCount === 0) stage = "identify";
        else if (newCount === 1) stage = "impact";
        else stage = "ask";
        return { ...d, renewalContactCount: newCount, stage };
      })
    );
  };

  // ── Candidate derivation ──────────────────────────────────────
  //
  // New Renewal rule:
  //   lifetime gift count >= 2
  //   AND has given at least once in the current fiscal year
  //
  // The "gave in current FY" clause automatically excludes LYBNT
  // (gave last FY, not this FY) and SYBNT (gave earlier than that,
  // not last FY, not this FY) donors — satisfying Glen's suppression
  // rule without any cross-card lookups.
  const renewalCandidates = useMemo(() => {
    if (!Array.isArray(allGifts) || allGifts.length === 0) return [];
    const boundaries = getFiscalYearBoundaries(
      chapterSettings?.fiscalYearStartMonth
    );

    // Bucket all gifts by member
    const giftsByMember = {};
    allGifts.forEach((g) => {
      const mid = g.member_id;
      if (!mid) return;
      if (!giftsByMember[mid]) giftsByMember[mid] = [];
      giftsByMember[mid].push(g);
    });

    const candidates = [];
    Object.entries(giftsByMember).forEach(([memberId, gifts]) => {
      if (gifts.length < 2) return; // Renewals = 2+ lifetime gifts
      const giftDates = gifts.map((g) => g.gift_date).filter(Boolean);
      const { gaveCurrentFy } = summarizeGiftsByFy(giftDates, boundaries);
      if (!gaveCurrentFy) return; // Excludes LYBNT and SYBNT automatically

      // Most-recent gift drives anniversary proximity — the default sort.
      const sortedGifts = [...gifts].sort((a, b) =>
        (b.gift_date || "").localeCompare(a.gift_date || "")
      );
      const lastGift = sortedGifts[0];
      const anniversaryDate = new Date(lastGift.gift_date + "T12:00:00");
      anniversaryDate.setFullYear(anniversaryDate.getFullYear() + 1);
      const anniversaryStr = anniversaryDate.toISOString().split("T")[0];

      const lifetimeTotal = gifts.reduce(
        (sum, g) => sum + (Number(g.amount) || 0),
        0
      );

      candidates.push({
        memberId,
        giftCount: gifts.length,
        lastGiftDate: lastGift.gift_date,
        lastGiftAmount: Number(lastGift.amount) || 0,
        lifetimeTotal,
        anniversaryDate: anniversaryStr,
        daysToAnniversary: daysFromToday(anniversaryStr),
      });
    });
    return candidates;
  }, [allGifts, chapterSettings?.fiscalYearStartMonth]);

  // Fetch contact logs with gift_renewal purpose (still needs independent query)
  useEffect(() => {
    const load = async () => {
      if (renewalCandidates.length === 0) {
        setRenewalDonors([]);
        return;
      }
      try {
        // Renewal-stage inference runs over the CURRENT fiscal year's
        // contact history — an identify/impact/ask workflow resets
        // each FY, so a gift_renewal contact logged in a prior FY
        // shouldn't bump this year's stage.
        const { currentFyStart } = getFiscalYearBoundaries(
          chapterSettings?.fiscalYearStartMonth
        );
        const windowStartStr = currentFyStart;
        const PAGE_SIZE = 1000;
        const fetchAll = async (query) => {
          const allRows = [];
          let offset = 0;
          while (true) {
            const { data: page, error } = await query().range(offset, offset + PAGE_SIZE - 1);
            if (error || !page) break;
            allRows.push(...page);
            if (page.length < PAGE_SIZE) break;
            offset += PAGE_SIZE;
          }
          return allRows;
        };

        const renewalContacts = await fetchAll(() =>
          supabase
            .from("contact_logs")
            .select("member_id, log_date, purpose")
            .gte("log_date", windowStartStr)
            .contains("purpose", ["gift_renewal"])
            // Stable order is required for safe .range() pagination.
            .order("id", { ascending: true })
        );

        const renewalContactCount = {};
        renewalContacts.forEach((log) => {
          renewalContactCount[log.member_id] = (renewalContactCount[log.member_id] || 0) + 1;
        });

        const stageOrder = { identify: 0, impact: 1, ask: 2 };
        const results = renewalCandidates.map((donor) => {
          const count = renewalContactCount[donor.memberId] || 0;
          let stage;
          if (count === 0) stage = "identify";
          else if (count === 1) stage = "impact";
          else stage = "ask";
          return { ...donor, stage, renewalContactCount: count };
        });

        results.sort((a, b) => {
          const stageDiff = stageOrder[a.stage] - stageOrder[b.stage];
          if (stageDiff !== 0) return stageDiff;
          return a.daysToAnniversary - b.daysToAnniversary;
        });

        setRenewalDonors(results);
      } catch (err) {
        // silent
      }
    };
    load();
  }, [renewalCandidates, dataVersion, chapterSettings?.fiscalYearStartMonth]);

  const getDonorName = (donor) => {
    const member = members.find((m) => m.id === donor.memberId);
    if (!member) return "—";
    if (member.donorType === "organization") {
      return member.organization || "Organization";
    }
    return (
      member.credentialedFullName ||
      `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
      "—"
    );
  };

  const unactionedCount = renewalDonors.filter((d) => d.stage !== "ask").length;

  // Format days indicator
  const fmtDays = (days) => {
    if (days === 0) return "today";
    if (days > 0) return `in ${days}d`;
    return `${Math.abs(days)}d ago`;
  };

  return (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "none",
          background: palette.background,
          borderRadius: "10px 10px 0 0",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <CalendarClock size={ICON_SIZES.sm} style={{ color: "#fff" }} />
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 700,
              color: "#fff",
              fontFamily: FONT_FAMILY,
            }}
          >
            Renewals
          </span>
          {renewalDonors.length > 0 && (
            <span
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 6,
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontFamily: FONT_FAMILY,
              }}
            >
              {unactionedCount} / {renewalDonors.length}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            onClick={() => setShowInfo((prev) => !prev)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 2,
              display: "flex",
              alignItems: "center",
              opacity: showInfo ? 1 : 0.5,
              transition: "opacity 0.15s",
            }}
            title="How this card works"
          >
            <Info size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
          </button>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 2,
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
          </button>
        </div>
      </div>

      {/* Info box */}
      {showInfo && (
        <div
          style={{
            padding: "10px 16px",
            fontSize: FONT_SIZES.xs,
            lineHeight: 1.5,
            color: palette.text2,
            fontFamily: FONT_FAMILY,
            background: `${palette.accent1}06`,
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          <strong>Anniversary renewals</strong> — donors whose last gift was
          approximately one year ago (±30 days). Workflow stages advance as you
          log contacts with the <strong>Gift Renewal</strong> purpose:{" "}
          <span style={{ color: STAGE_COLORS.identify }}>●</span> Identify →{" "}
          <span style={{ color: STAGE_COLORS.impact }}>●</span> Impact Report →{" "}
          <span style={{ color: STAGE_COLORS.ask }}>●</span> Ask.
          The badge shows <strong>unfinished / total</strong>.
        </div>
      )}

      {/* Subtitle */}
      <div
        style={{
          padding: "8px 16px 4px",
          fontSize: "0.65rem",
          color: palette.text3,
          fontFamily: FONT_FAMILY,
          fontStyle: "italic",
        }}
      >
        Anniversary donors — time to renew the relationship.
      </div>

      {/* Donor list — scrollable */}
      {renewalDonors.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: palette.text3,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
          }}
        >
          No renewal anniversaries right now.
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "4px 0",
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          {renewalDonors.map((donor) => {
            const isComplete = donor.stage === "ask";
            const stageColor = STAGE_COLORS[donor.stage];
            return (
              <div
                key={donor.memberId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 16px",
                  transition: "background 0.1s",
                  opacity: isComplete ? 0.55 : 1,
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = `${palette.accent1}08`;
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = "transparent";
                }}
              >
                {/* Stage indicator dot */}
                <span
                  title={STAGE_LABELS[donor.stage]}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: stageColor,
                    flexShrink: 0,
                  }}
                />

                {/* Name — clickable */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    cursor: members.find((m) => m.id === donor.memberId) ? "pointer" : "default",
                  }}
                  onClick={() => {
                    const fullMember = members.find((m) => m.id === donor.memberId);
                    if (fullMember)
                      navigate("memberDetail", fullMember, {
                        from: "renewals",
                        suggestedTemplate: "renewal_ask",
                      });
                  }}
                >
                  <div
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontWeight: isComplete ? 400 : 600,
                      color: isComplete ? palette.text3 : palette.text1,
                      fontFamily: FONT_FAMILY,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {getDonorName(donor)}
                  </div>
                </div>

                {/* Cultivation queue indicator */}
                {cultivationQueue.some((q) => q.memberId === donor.memberId) && (
                  <Sprout size={ICON_SIZES.xs} style={{ color: "#10b981", flexShrink: 0 }} title="In cultivation queue" />
                )}

                {/* Quick-action: advance stage */}
                {!isComplete && (
                  <button
                    onClick={(event) => handleMarkContacted(donor, event)}
                    title="Mark contacted (logs Gift Renewal)"
                    style={{
                      background: "none",
                      border: `1px solid ${palette.cardBorder}`,
                      borderRadius: 4,
                      cursor: "pointer",
                      padding: 2,
                      display: "flex",
                      alignItems: "center",
                      opacity: 0.4,
                      transition: "opacity 0.15s",
                      flexShrink: 0,
                    }}
                    onMouseEnter={(event) => { event.currentTarget.style.opacity = "1"; }}
                    onMouseLeave={(event) => { event.currentTarget.style.opacity = "0.4"; }}
                  >
                    <Check size={ICON_SIZES.xs} style={{ color: "#10b981" }} />
                  </button>
                )}

                {/* Stage label */}
                <span
                  style={{
                    fontSize: "0.5rem",
                    fontWeight: 600,
                    padding: "1px 5px",
                    borderRadius: 4,
                    background: `${stageColor}15`,
                    color: stageColor,
                    fontFamily: FONT_FAMILY,
                    flexShrink: 0,
                  }}
                >
                  {STAGE_LABELS[donor.stage]}
                </span>

                {/* Days to/from anniversary */}
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: donor.daysToAnniversary < 0 ? "#ef4444" : palette.text3,
                    fontWeight: donor.daysToAnniversary < 0 ? 600 : 400,
                    fontFamily: FONT_FAMILY,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {fmtDays(donor.daysToAnniversary)}
                </span>

                {/* Last gift amount */}
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 600,
                    color: isComplete ? palette.text3 : "#0d9488",
                    fontFamily: FONT_FAMILY,
                    flexShrink: 0,
                  }}
                >
                  {fmtCurrency(donor.lastGiftAmount)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
